import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { VIDEOS } from "@/lib/business";

export const Videos = () => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (reduceMotion) {
      video.pause();
      setPlaying(false);
      return;
    }
    video
      .play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false));
  }, [index, reduceMotion]);

  if (!VIDEOS.length) return null;

  const current = VIDEOS[index];
  const go = (dir) =>
    setIndex((i) => (i + dir + VIDEOS.length) % VIDEOS.length);
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      id="videos"
      data-testid="videos-section"
      className="scroll-mt-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl" data-reveal>
          <p
            data-testid="videos-overline"
            className="text-xs font-bold uppercase tracking-[0.2em] text-brand"
          >
            Em Destaque
          </p>
          <h2
            data-testid="videos-heading"
            className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900"
          >
            Estilo que se vê em movimento
          </h2>
        </div>

        <div data-reveal className="mt-12 max-w-3xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-stone-200 shadow-lg bg-stone-900">
            <video
              key={current.src}
              ref={videoRef}
              data-testid="videos-player"
              src={current.src}
              poster={current.poster}
              aria-label={current.alt}
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full aspect-video object-cover"
              onClick={togglePlay}
            />
            <button
              type="button"
              onClick={togglePlay}
              data-testid="videos-play-toggle"
              aria-label={playing ? "Pausar vídeo" : "Reproduzir vídeo"}
              className="absolute bottom-4 right-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/90 text-stone-900 shadow-md hover:bg-white btn-motion focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              {playing ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>
          </div>

          {VIDEOS.length > 1 && (
            <div className="mt-6 flex items-center justify-center gap-6">
              <button
                type="button"
                onClick={() => go(-1)}
                data-testid="videos-prev-button"
                aria-label="Vídeo anterior"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-stone-300 text-stone-700 hover:border-brand hover:text-brand btn-motion focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div
                data-testid="videos-dots"
                className="flex items-center gap-2"
              >
                {VIDEOS.map((video, i) => (
                  <button
                    key={video.src}
                    type="button"
                    onClick={() => setIndex(i)}
                    data-testid={`videos-dot-${i + 1}`}
                    aria-label={`Ir para o vídeo ${i + 1}`}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      i === index ? "bg-brand" : "bg-stone-300 hover:bg-stone-400"
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => go(1)}
                data-testid="videos-next-button"
                aria-label="Próximo vídeo"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-stone-300 text-stone-700 hover:border-brand hover:text-brand btn-motion focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
